import { KAKI_MAKAN_EVENT_CATEGORY } from "@config/kakiMakan";
import debounce, { DebouncedFn } from "@helper/debounce";
import { useCallback, useMemo } from "react";

import { RestaurantSearchProps } from "../RestaurantSearch";

type useRestaurantSearchData = {
  debouncedSearch: DebouncedFn<[string]>;
  handleKeyPress: (value: string) => void;
};

type useRestaurantSearchProps = RestaurantSearchProps & {
  bhCustomEvent: ReturnType<
    (typeof import("@hooks/useAnalyticsCall/useBHCustomEventAnalyticsCall"))["useBHCustomEventAnalyticsCall"]
  >;
};

/**
 * Custom React Hook: useRestaurantSearchHook
 * Description: [Description of what the hook does]
 * @param props - The props required by the hook (if any)
 * @returns data - The data returned by the hook
 */
export default function useRestaurantSearch({
  onSearch,
  bhCustomEvent,
}: useRestaurantSearchProps): useRestaurantSearchData {
  // Debounce the onSearch function to limit the number of calls
  // wait for 500ms after the user stops typing before calling onSearch
  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        onSearch(value);
        if (value) {
          bhCustomEvent(KAKI_MAKAN_EVENT_CATEGORY, "search", value);
        }
      }, 1000), // wait for 1 second
    [onSearch, bhCustomEvent],
  );

  const handleKeyPress = useCallback(
    (value: string) => {
      onSearch(value);
      if (value) {
        bhCustomEvent(KAKI_MAKAN_EVENT_CATEGORY, "search", value);
      }
    },
    [onSearch, bhCustomEvent],
  );

  return {
    debouncedSearch,
    handleKeyPress,
  };
}
